import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Users, Flame, Lock, CheckCircle2, Rocket, Loader2, PlayCircle, BadgeCheck, Zap } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const DAY_XP_REWARD = 40;

function CustomDayCard({ d, idx, challengeId, isCurrent, quizScore }) {
  const isLocked = d.status === "locked";
  const isCompleted = d.status === "completed";
  const CardWrapper = isLocked ? "div" : Link;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: Math.min(idx * 0.05, 0.4) }}>
      <CardWrapper
        to={isLocked ? undefined : `/custom-challenges/${challengeId}/day/${d.day}`}
        data-testid={`custom-challenge-day-card-${d.day}`}
        className={`relative rounded-xl border p-5 transition-all duration-300 block h-full ${
          isLocked ? "border-white/5 bg-white/[0.01] opacity-50 cursor-not-allowed"
          : isCompleted ? "border-emerald-400/30 bg-emerald-400/[0.03] hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(16,185,129,0.15)]"
          : "border-[#5C9DFF]/30 bg-white/[0.02] hover:border-[#5C9DFF]/60 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(92,157,255,0.15)]"
        } ${isCurrent ? "ring-2 ring-[#5C9DFF] animate-pulse" : ""}`}
      >
        {isCurrent && (
          <span className="absolute -top-2.5 left-4 text-[9px] font-bold uppercase tracking-wider bg-[#5C9DFF] text-white px-2 py-0.5 rounded-full" data-testid={`custom-challenge-current-badge-${d.day}`}>
            Continue Here
          </span>
        )}
        <div className="flex items-center justify-between mb-3">
          <span className="font-heading text-xs font-bold text-slate-400">DAY {d.day}</span>
          {isLocked ? <Lock className="w-4 h-4 text-slate-400" />
            : isCompleted ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            : <PlayCircle className="w-4 h-4 text-[#5C9DFF]" />}
        </div>
        <h3 className={`font-medium text-sm mb-3 ${isLocked ? "text-slate-400" : "text-white"}`}>{d.title}</h3>
        <span className="text-[10px] text-amber-300 flex items-center gap-1"><Zap className="w-3 h-3" /> {DAY_XP_REWARD} XP</span>
        {isCompleted && quizScore != null && <div className="mt-3 text-xs text-emerald-400 font-semibold">Score: {quizScore}%</div>}
      </CardWrapper>
    </motion.div>
  );
}

export default function CustomChallengeDetailPage() {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/custom-challenges/${challengeId}`);
      setResult(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Unable to load this challenge.");
    } finally {
      setLoading(false);
    }
  }, [challengeId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="custom-challenge-detail-loading">
        <Loader2 className="w-6 h-6 text-[#5C9DFF] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="custom-challenge-detail-error">
        <p className="text-slate-300">{error}</p>
        <Link to="/marketplace" className="text-indigo-400 hover:text-indigo-300 text-sm">Back to Marketplace</Link>
      </div>
    );
  }

  const { challenge, days, progress } = result;
  const completedCount = progress.completed_days.length;
  const progressPct = Math.round((completedCount / challenge.total_days) * 100);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="custom-challenge-detail-page">
      <button onClick={() => navigate("/marketplace")} data-testid="custom-challenge-back-btn" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Marketplace
      </button>

      <div className="glass-panel rounded-2xl p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C9DFF]">{challenge.category}</span>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white mt-2" data-testid="custom-challenge-title">{challenge.title}</h1>
            <p className="text-sm text-slate-400 mt-2 max-w-xl">{challenge.description}</p>
            <div className="flex items-center gap-4 mt-3 text-xs text-slate-400 flex-wrap">
              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {challenge.learners_count} learner{challenge.learners_count === 1 ? "" : "s"}</span>
              <span className="flex items-center gap-1"><Flame className="w-3.5 h-3.5 text-orange-400" /> {progress.streak_current} day streak</span>
              <span>By {challenge.creator_name}</span>
              {challenge.is_published && (
                <span className="flex items-center gap-1 text-emerald-300"><BadgeCheck className="w-3.5 h-3.5" /> Published</span>
              )}
            </div>
          </div>
          {challenge.is_creator && !challenge.is_published && (
            <Link
              to={`/creator/challenges/${challengeId}/builder`} data-testid="custom-challenge-open-builder-btn"
              className="shrink-0 flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-5 py-2.5 rounded-xl transition-all"
            >
              <Rocket className="w-4 h-4" /> Open Builder
            </Link>
          )}
        </div>

        <div className="mt-5">
          <div className="flex justify-between text-xs text-slate-400 mb-1.5">
            <span>Progress ({completedCount}/{challenge.total_days} days)</span>
            <span className="font-mono" data-testid="custom-challenge-progress-pct">{progressPct}%</span>
          </div>
          <div className="w-full h-2.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[#5C9DFF] to-[#B58BFF]" style={{ width: `${Math.max(progressPct, 3)}%` }} />
          </div>
        </div>
      </div>

      <div className="space-y-8" data-testid="custom-challenge-day-grid">
        {(() => {
          const currentDay = days.find((d) => d.status === "unlocked")?.day;
          const weekCount = Math.ceil(days.length / 7);
          return Array.from({ length: weekCount }, (_, w) => {
            const start = w * 7 + 1;
            const end = Math.min(start + 6, days.length);
            const weekDays = days.filter((d) => d.day >= start && d.day <= end);
            if (weekDays.length === 0) return null;
            const weekCompleted = weekDays.filter((d) => d.status === "completed").length;
            return (
              <div key={w} data-testid={`custom-challenge-week-${w + 1}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C9DFF]">Week {w + 1}</span>
                    <span className="text-sm font-semibold text-white">Day {start}–{end}</span>
                  </div>
                  <span className="text-[11px] text-slate-400">{weekCompleted}/{weekDays.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {weekDays.map((d, idx) => (
                    <CustomDayCard key={d.day} d={d} idx={idx} challengeId={challengeId} isCurrent={d.day === currentDay} quizScore={progress.quiz_scores?.[d.day]} />
                  ))}
                </div>
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
}
